/**
 * Write actions in chat (MHS-971, docs/design/extensions-integrations/WRITE_ACTIONS_DESIGN.md). When the model calls a
 * tool that changes something outside Anthm (send an email, post to Slack, create an issue), nothing runs: the reply
 * carries the call as a proposal (`messageMetadata.tool_confirmation`, live as a `tool_confirmation` event) with the
 * exact arguments, and the chat's owner approves or declines it on a card. Approving runs exactly those arguments.
 */

import type { SrcSchemasChatMessageResponse, ToolConfirmationData } from "@/api";
import { formatList } from "@/i18n/format";
import { type MeetingDay, meetingDayOf } from "../calendar/calendarModel";
import { RESEARCH_TOOL } from "../research/researchModel";

type Metadata = SrcSchemasChatMessageResponse["messageMetadata"];

const STATUSES = ["pending", "confirmed", "declined", "superseded"] as const;
export type ActionStatus = (typeof STATUSES)[number];

/** Builtin write actions with a title of their own; any other tool (a connector's) gets a generic one. */
export const NAMED_ACTIONS = [
  "send_email",
  "slack_send_message",
  "webhook_call",
  "asana_create_task",
  "jira_create_issue",
  "jira_update_issue",
  "jira_add_comment",
  "create_calendar_event",
  "send_outlook_email",
  "send_outlook_draft",
] as const;
export type NamedAction = (typeof NAMED_ACTIONS)[number];

/** One argument as the card lists it: its name as the tool has it, and its value as text. */
export interface ActionField {
  name: string;
  value: string;
  /** Long or structured text (a message body, a JSON payload): shown as a block that keeps its line breaks. */
  block: boolean;
}

export const DESTINATION_KINDS = ["dm", "group_dm", "channel"] as const;
export type DestinationKind = (typeof DESTINATION_KINDS)[number];

/** Where the action goes, as the server resolved it (never a label the model wrote). */
export interface ActionDestination {
  kind: DestinationKind;
  /** "Michael Koert", "#launch"; null when only the kind is known (a group DM). */
  label: string | null;
  /** A secondary line, e.g. "@michael.koert". */
  detail: string | null;
}

/** Someone a meeting invites or an email goes to; `external` when they're outside the person's company. */
export interface ActionPerson {
  address: string;
  role: "attendee" | "to" | "cc";
  external: boolean;
}

/** When a meeting is (ISO with offset, so it reads right in any time zone). */
export interface ActionWhen {
  start: string;
  end: string | null;
}

/** What an action does, as a card shows it: its title, where it goes, and the values it will send. */
export interface ActionDescription {
  /** The tool as the model called it. */
  tool: string;
  /** Its builtin name when it has a title of its own, else null. */
  named: NamedAction | null;
  /** The tool's own name for the generic title: a connector's tool without its `cx<id>_` prefix. */
  displayName: string;
  /** From the server's preview (`details.preview.target`), when it found where the action goes. */
  destination: ActionDestination | null;
  /** A reply in a thread rather than a new message. */
  inThread: boolean;
  fields: ActionField[];
  /** The text fields people may change before approving (never where it goes), with their current text. */
  editable: Record<string, string>;
  /** Can be approved for the rest of the chat (the server named where it goes). */
  rememberable: boolean;
  /** A meeting's time, from the server's preview. */
  when: ActionWhen | null;
  /** Who it invites or reaches, from the server's preview. */
  people: ActionPerson[];
  /** A meeting with a Teams link. */
  online: boolean;
  /** The preview's kind ("meeting", "email"), which picks any content of its own the card shows. */
  kind: string | null;
  /** A meeting's day: everyone's busy time around it. */
  day: MeetingDay | null;
}

/** What happened when the approved call ran: done (with a link to what it made) or not. */
export interface ActionReceipt {
  ok: boolean;
  url: string | null;
}

export interface ActionProposal extends ActionDescription {
  /** The confirmation id a decision names. */
  id: string;
  status: ActionStatus;
  /** Approved for the rest of this chat. */
  remembered: boolean;
  receipt: ActionReceipt | null;
}

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const oneOf = <T extends string>(values: readonly T[], value: unknown): value is T =>
  typeof value === "string" && (values as readonly string[]).includes(value);

const BLOCK_CHARS = 80;

/** Markup in an HTML body shown as its text, with paragraphs and line breaks kept. */
function textOfHtml(html: string): string {
  const doc = new DOMParser().parseFromString(
    html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h\d)>/gi, "\n"),
    "text/html",
  );
  return (doc.body.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
}

function fieldOf(name: string, raw: unknown): ActionField | null {
  if (raw === null || raw === undefined || raw === "") return null;
  if (Array.isArray(raw) && raw.every((item) => typeof item === "string")) {
    if (raw.length === 0) return null;
    return { name, value: formatList(raw), block: false };
  }
  if (typeof raw === "string") {
    const value = name.endsWith("_html") ? textOfHtml(raw) : raw;
    return { name, value, block: value.includes("\n") || value.length > BLOCK_CHARS };
  }
  if (typeof raw === "number" || typeof raw === "boolean") return { name, value: String(raw), block: false };
  return { name, value: JSON.stringify(raw, null, 2), block: true };
}

function destinationOf(raw: unknown): ActionDestination | null {
  const target = record(raw);
  if (!oneOf(DESTINATION_KINDS, target.kind)) return null;
  const text = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
  return { kind: target.kind, label: text(target.label), detail: text(target.detail) };
}

const PEOPLE_ROLES = ["attendee", "to", "cc"] as const;

function peopleOf(raw: unknown): ActionPerson[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const person = record(item);
    if (typeof person.address !== "string" || !oneOf(PEOPLE_ROLES, person.role)) return [];
    return [{ address: person.address, role: person.role, external: person.external === true }];
  });
}

function whenOf(raw: unknown): ActionWhen | null {
  const when = record(raw);
  if (typeof when.start !== "string") return null;
  return { start: when.start, end: typeof when.end === "string" ? when.end : null };
}

/**
 * A tool call described for its card: the chat's approval card and the approvals inbox read it the same way. The
 * server's preview (`details.preview`, MHS-971) adds where it goes and names the arguments the card leaves out.
 */
export function describeAction(tool: string, rawArguments: unknown, details?: unknown): ActionDescription {
  const builtin = tool.replace(/^cx\d+_/, "");
  const preview = record(record(details).preview);
  const hidden = new Set(Array.isArray(preview.hidden) ? preview.hidden.filter((h) => typeof h === "string") : []);
  // A draft's subject and message, read from the mailbox, are shown as if they were arguments.
  const args = { ...record(rawArguments), ...record(preview.content) };
  const editable: Record<string, string> = {};
  for (const name of Array.isArray(preview.editable) ? preview.editable : []) {
    const value = args[String(name)];
    if (typeof value === "string") editable[String(name)] = value;
  }
  return {
    tool,
    named: oneOf(NAMED_ACTIONS, builtin) ? builtin : null,
    displayName: builtin,
    destination: destinationOf(preview.target),
    inThread: preview.in_thread === true,
    fields: Object.entries(args).flatMap(([name, value]) => (hidden.has(name) ? [] : (fieldOf(name, value) ?? []))),
    editable,
    rememberable: preview.rememberable === true,
    when: whenOf(preview.when),
    people: peopleOf(preview.people),
    online: preview.online === true,
    kind: typeof preview.kind === "string" ? preview.kind : null,
    day: meetingDayOf(preview.day),
  };
}

function receiptOf(raw: unknown): ActionReceipt | null {
  const receipt = record(raw);
  if (typeof receipt.ok !== "boolean") return null;
  const url = typeof receipt.url === "string" && receipt.url.startsWith("https://") ? receipt.url : null;
  return { ok: receipt.ok, url };
}

function actionOf(
  id: unknown,
  tool: unknown,
  rawArguments: unknown,
  status: unknown,
  details: unknown,
  saved: Record<string, unknown> = {},
): ActionProposal | null {
  if (typeof id !== "string" || typeof tool !== "string" || tool === RESEARCH_TOOL) return null;
  return {
    id,
    // A status this client doesn't know can't be acted on.
    status: oneOf(STATUSES, status) ? status : "superseded",
    ...describeAction(tool, rawArguments, details),
    remembered: saved.remember === true,
    receipt: receiptOf(saved.receipt),
  };
}

/** The write action a saved reply proposed, or null (none, or a research proposal). */
export function actionFromMetadata(metadata: Metadata): ActionProposal | null {
  const saved = record(metadata?.tool_confirmation);
  return actionOf(saved.id, saved.tool, saved.arguments, saved.status, saved.details, saved);
}

/** The write action a reply just proposed (pending until decided), or null. */
export function actionFromEvent(data: ToolConfirmationData): ActionProposal | null {
  return actionOf(data.confirmation_id, data.tool, data.arguments, "pending", record(data).details);
}
